/**
 * @fileoverview Document Extracted Content View
 *
 * Purpose: Renders the extracted content of a document using paginated
 * chunk loading. Each page fetches ~50 chunks to stay well under
 * Convex's 16MB read limit (each chunk row includes a ~12KB embedding).
 *
 * Shows text, tables, and figures from the Reducto extraction,
 * organised by chunk with page numbers. Parses embedded HTML,
 * markdown tables, and markdown headings into styled elements.
 * Each chunk is collapsible, and pages auto-load on scroll.
 *
 * @component
 */

import DOMPurify from "dompurify";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { usePaginatedQuery } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import {
  FileText,
  Table2,
  Image,
  Loader2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/ui/primitives/button";

interface DocumentPreviewProps {
  documentId: Id<"documents">;
}

const CHUNK_TYPE_ICONS = {
  text: FileText,
  table: Table2,
  figure: Image,
} as const;

const CHUNK_TYPE_LABELS = {
  text: "Text",
  table: "Table",
  figure: "Figure",
} as const;

const PAGE_SIZE = 50;

/**
 * Renders the extracted content chunks for a document using paginated loading.
 * Chunks auto-load as the user scrolls, with a manual "Load more" fallback.
 */
export function DocumentPreview({ documentId }: DocumentPreviewProps) {
  const {
    results: chunks,
    status,
    loadMore,
  } = usePaginatedQuery(
    api.jobs.gatherInformation.indexing.queries.getDocumentChunks,
    { documentId },
    { initialNumItems: PAGE_SIZE },
  );

  const isFirstPage = status === "LoadingFirstPage";
  const canLoadMore = status === "CanLoadMore";
  const isLoadingMore = status === "LoadingMore";
  const isExhausted = status === "Exhausted";

  const [collapsed, setCollapsed] = useState<Set<number>>(() => new Set());
  const [globalOverride, setGlobalOverride] = useState<
    "all-expanded" | "all-collapsed" | null
  >(null);

  const toggleChunk = useCallback((index: number) => {
    setGlobalOverride(null);
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const collapseAll = useCallback(() => {
    setGlobalOverride("all-collapsed");
  }, []);

  const expandAll = useCallback(() => {
    setGlobalOverride("all-expanded");
  }, []);

  const isCollapsed = useCallback(
    (index: number) => {
      if (globalOverride === "all-collapsed") return true;
      if (globalOverride === "all-expanded") return false;
      return collapsed.has(index);
    },
    [collapsed, globalOverride],
  );

  // Auto-load next page when sentinel scrolls into view
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!canLoadMore) return;
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore(PAGE_SIZE);
        }
      },
      { rootMargin: "400px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [canLoadMore, loadMore]);

  if (isFirstPage) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!chunks || chunks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <FileText className="h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">
          No extracted content available yet.
        </p>
        <p className="text-xs text-muted-foreground">
          Content will appear here once document processing is complete.
        </p>
      </div>
    );
  }

  const countLabel = isExhausted
    ? `${chunks.length} chunk${chunks.length !== 1 ? "s" : ""} extracted`
    : `${chunks.length} chunks loaded…`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{countLabel}</span>
        <div className="flex gap-2">
          <button
            onClick={expandAll}
            className="text-[10px] text-muted-foreground transition-colors hover:text-foreground"
          >
            Expand all
          </button>
          <span className="text-[10px] text-muted-foreground">|</span>
          <button
            onClick={collapseAll}
            className="text-[10px] text-muted-foreground transition-colors hover:text-foreground"
          >
            Collapse all
          </button>
        </div>
      </div>

      {chunks.map((chunk, index) => {
        const Icon =
          CHUNK_TYPE_ICONS[chunk.chunkType as keyof typeof CHUNK_TYPE_ICONS] ??
          FileText;
        const typeLabel =
          CHUNK_TYPE_LABELS[
            chunk.chunkType as keyof typeof CHUNK_TYPE_LABELS
          ] ?? "Content";
        const chunkCollapsed = isCollapsed(index);

        return (
          <div key={chunk._id} className="rounded-lg border">
            <button
              onClick={() => toggleChunk(index)}
              className="flex w-full items-center gap-2 px-3 py-2 transition-colors hover:bg-muted/50"
            >
              {chunkCollapsed ? (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              )}
              <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <Badge variant="outline" size="sm">
                {typeLabel}
              </Badge>
              {chunk.pageNumber !== undefined && (
                <span className="text-[10px] text-muted-foreground">
                  Page {chunk.pageNumber}
                </span>
              )}
              {chunkCollapsed && (
                <span className="ml-auto max-w-[50%] truncate text-[10px] text-muted-foreground">
                  {chunk.content
                    .slice(0, 80)
                    .replace(/[#|<>\n]/g, " ")
                    .trim()}
                  …
                </span>
              )}
            </button>
            {!chunkCollapsed && (
              <div className="border-t px-3 py-3">
                <ChunkContent
                  content={chunk.content}
                  chunkType={chunk.chunkType}
                />
              </div>
            )}
          </div>
        );
      })}

      {/* Infinite-scroll sentinel */}
      <div ref={sentinelRef} />

      {isLoadingMore && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="mr-2 h-4 w-4 animate-spin text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            Loading more chunks…
          </span>
        </div>
      )}
      {canLoadMore && !isLoadingMore && (
        <div className="flex justify-center py-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadMore(PAGE_SIZE)}
            className="text-xs"
          >
            Load more chunks
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chunk Content Renderer
// ---------------------------------------------------------------------------

function ChunkContent({
  content,
  chunkType: _chunkType,
}: {
  content: string;
  chunkType: string;
}) {
  const hasHtmlTable = /<table\b/i.test(content);
  const hasMarkdownTable = /^\|.+\|$/m.test(content);
  const hasBrTags = /<br\s*\/?>/i.test(content);

  if (hasHtmlTable) {
    return <HtmlContent content={content} />;
  }

  if (hasMarkdownTable) {
    return <MarkdownContent content={content} />;
  }

  if (hasBrTags) {
    return <BrTagContent content={content} />;
  }

  return <PlainTextContent content={content} />;
}

// ---------------------------------------------------------------------------
// HTML Table Renderer
// ---------------------------------------------------------------------------

function HtmlContent({ content }: { content: string }) {
  const processedHtml = useMemo(() => {
    const firstTagIndex = content.search(/<[a-zA-Z]/);
    if (firstTagIndex <= 0) return { heading: "", htmlPart: content };

    const beforeTag = content.slice(0, firstTagIndex).trim();
    if (!beforeTag) return { heading: "", htmlPart: content };

    const headingMatch = beforeTag.match(/^#+\s+(.+)/);
    return {
      heading: headingMatch ? headingMatch[1] : beforeTag,
      htmlPart: content.slice(firstTagIndex),
    };
  }, [content]);

  return (
    <div className="flex flex-col gap-2">
      {processedHtml.heading && (
        <h4 className="text-sm font-semibold">{processedHtml.heading}</h4>
      )}
      <div className="overflow-x-auto rounded-md border">
        <div
          className="preview-html-content"
          // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- DOMPurify.sanitize applied inline
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(processedHtml.htmlPart),
          }}
        />
        <style>{`
          .preview-html-content table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.75rem;
            line-height: 1.25rem;
          }
          .preview-html-content th {
            background-color: hsl(var(--muted));
            font-weight: 600;
            text-align: left;
            padding: 0.5rem 0.75rem;
            border-bottom: 2px solid hsl(var(--border));
            white-space: nowrap;
            font-size: 0.7rem;
            text-transform: uppercase;
            letter-spacing: 0.025em;
            color: hsl(var(--muted-foreground));
          }
          .preview-html-content td {
            padding: 0.375rem 0.75rem;
            border-bottom: 1px solid hsl(var(--border));
            vertical-align: top;
          }
          .preview-html-content tr:hover td {
            background-color: hsl(var(--muted) / 0.5);
          }
          .preview-html-content tr:last-child td {
            border-bottom: none;
          }
        `}</style>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Markdown Table Renderer
// ---------------------------------------------------------------------------

function MarkdownContent({ content }: { content: string }) {
  const { sections } = useMemo(() => {
    const lines = content.split("\n");
    const result: Array<
      | { type: "heading"; text: string }
      | { type: "text"; text: string }
      | { type: "table"; headers: string[]; rows: string[][] }
    > = [];

    let i = 0;
    while (i < lines.length) {
      const line = lines[i].trim();

      if (line.startsWith("#")) {
        const match = line.match(/^(#+)\s+(.+)/);
        if (match) {
          result.push({ type: "heading", text: match[2] });
          i++;
          continue;
        }
      }

      if (line.startsWith("|") && line.endsWith("|")) {
        const tableLines: string[] = [];
        while (
          i < lines.length &&
          lines[i].trim().startsWith("|") &&
          lines[i].trim().endsWith("|")
        ) {
          tableLines.push(lines[i].trim());
          i++;
        }

        if (tableLines.length >= 1) {
          const parsedRows = tableLines
            .filter((tl) => !tl.match(/^\|[\s-:|]+\|$/))
            .map((tl) =>
              tl
                .split("|")
                .slice(1, -1)
                .map((cell) => cell.trim()),
            );

          if (parsedRows.length > 0) {
            const headers = parsedRows[0];
            const dataRows = parsedRows.slice(1);
            result.push({ type: "table", headers, rows: dataRows });
          }
        }
        continue;
      }

      if (line) {
        result.push({ type: "text", text: line });
      }
      i++;
    }

    return { sections: result };
  }, [content]);

  return (
    <div className="flex flex-col gap-3">
      {sections.map((section, idx) => {
        if (section.type === "heading") {
          return (
            <h4 key={idx} className="text-sm font-semibold">
              {section.text}
            </h4>
          );
        }

        if (section.type === "text") {
          return (
            <p key={idx} className="text-xs leading-relaxed text-foreground/90">
              {section.text}
            </p>
          );
        }

        if (section.type === "table") {
          const isKV = section.headers.length === 2 && section.rows.length > 0;

          return (
            <div key={idx} className="overflow-x-auto rounded-md border">
              <table className="w-full text-xs">
                {!isKV && (
                  <thead>
                    <tr className="bg-muted/50">
                      {section.headers.map((h, hi) => (
                        <th
                          key={hi}
                          className="border-b-2 border-border px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {isKV && (
                    <tr className="transition-colors hover:bg-muted/30">
                      <td className="w-1/3 px-3 py-2 font-medium text-muted-foreground">
                        {section.headers[0]}
                      </td>
                      <td className="px-3 py-2">{section.headers[1]}</td>
                    </tr>
                  )}
                  {section.rows.map((row, ri) => (
                    <tr
                      key={ri}
                      className="border-t border-border/50 transition-colors hover:bg-muted/30"
                    >
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          className={`px-3 py-2 ${
                            isKV && ci === 0
                              ? "w-1/3 font-medium text-muted-foreground"
                              : ""
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// <br /> Tag Content Renderer
// ---------------------------------------------------------------------------

function BrTagContent({ content }: { content: string }) {
  const cleaned = content.replace(/<br\s*\/?>/gi, "\n");

  if (/^\|.+\|$/m.test(cleaned)) {
    return <MarkdownContent content={cleaned} />;
  }

  return <PlainTextContent content={cleaned} />;
}

// ---------------------------------------------------------------------------
// Plain Text Content Renderer
// ---------------------------------------------------------------------------

function PlainTextContent({ content }: { content: string }) {
  const lines = content.split("\n");

  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        const trimmed = line.trimStart();

        if (trimmed.startsWith("# ")) {
          return (
            <h3 key={i} className="pt-2 text-sm font-semibold">
              {trimmed.slice(2)}
            </h3>
          );
        }
        if (trimmed.startsWith("## ")) {
          return (
            <h4 key={i} className="pt-1.5 text-xs font-semibold">
              {trimmed.slice(3)}
            </h4>
          );
        }
        if (trimmed.startsWith("### ")) {
          return (
            <h5 key={i} className="pt-1 text-xs font-medium">
              {trimmed.slice(4)}
            </h5>
          );
        }

        if (!trimmed) {
          return <div key={i} className="h-2" />;
        }

        return (
          <p key={i} className="text-xs leading-relaxed text-foreground/90">
            {line}
          </p>
        );
      })}
    </div>
  );
}
